import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { useActingOrg } from '@/auth/actingOrgContext';
import {
    fetchOrganization,
    fetchOrganizations,
    fetchOrgLevels,
    fetchScopedAccounts,
    type Organization,
    type ScopedAccountIssue,
    type ScopedAccountRow,
    type ScopedAccountsParams,
} from '@/api/orgAdmin';
import { filterParams, orderingParam } from './scopedAccountsQuery';
import { useScopedAccountFacets } from './useScopedAccountFacets';
import {
    EMPTY_FILTERS,
    groupScopedAccountRows,
    type FacetGroupKey,
    type ScopedAccountGroupedRow,
    type ScopedAccountSortField,
    type ScopedAccountsFilters,
    type SortDirection,
} from './types';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

/** Server ceiling (`emg_shared.api.common.paginate_queryset`), not a preference. */
const MAX_SERVER_PAGE_SIZE = 100;

/**
 * EAA-1839: cap on the "Chọn tất cả M dòng khớp bộ lọc" walk. Same shape and same
 * reason as `accounts/useAccountsScan.MAX_SCAN_PAGES` and
 * `roleMap/useAccountCounts` — the server caps a page at 100 rows and offers no
 * "give me every id" endpoint, so selecting a whole filtered set means walking
 * pages. 20 × 100 = 2,000 rows; past that the walk stops and says so instead of
 * hanging the console on a very large đơn vị.
 */
const MAX_SELECT_ALL_PAGES = 20;

/**
 * A plain trailing-edge debounce over a VALUE — same shape as
 * `useProvisionAccount.ts`'s local copy in this folder. NOT the kit's
 * `useDebouncedSearch`: that debounces a controlled input's `onChange`
 * callback for `TableToolbar` and returns the immediate local value, so it
 * cannot gate a TanStack Query key the way this search needs.
 */
function useDebouncedValue(value: string, delay: number): string {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = window.setTimeout(() => setDebounced(value), delay);
        return () => window.clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

/**
 * EAA-1839: view model behind `/console/hierarchy/accounts` — the org-scoped
 * account browser of frame 34, plus everything the bulk Level-assign dialog
 * (frame 35, `useBulkLevelAssign`) needs handed to it.
 *
 * SCOPE COMES FROM THE URL (`?org=<uuid>`), falling back to the acting org. That
 * is what makes the "Xem tài khoản" action on a tree node a real deep link
 * instead of a page that ignores which đơn vị was selected.
 *
 * SERVER-SIDE, NOT CLIENT-SIDE. Unlike `/console/accounts` (which walks the whole
 * realm because its endpoint has no filters at all), every filter here IS a real
 * query param, so paging and filtering are the server's job and one page is one
 * request. The two client-side exceptions are both forced and both flagged in the
 * UI: the facet counts (no aggregate endpoint exists — one `page_size=1` request
 * per option) and "select all matching" (no bulk-id endpoint exists — a capped
 * page walk).
 *
 * PERMISSIONS mirror `useHierarchyPage` exactly, because this is the same
 * `/console/hierarchy` surface: `console.orgs.view` to read, and
 * `org:{actingOrgId}.manage` to write (bulk Level-assign, revoking a grant) —
 * the same gate the assignment-authoring endpoints enforce. The capability is
 * keyed to the ACTING org while the scope may be a descendant of it; portal-api
 * re-authorizes the scope org on every call (`_visible_org_or_none`, which is
 * `OrganizationAdmin`-row based), so a descendant the caller does not administer
 * answers 404 and the screen renders that error rather than an empty table.
 */
export function useScopedAccounts() {
    const { t } = useTranslation();
    const can = useCan();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();
    const acting = useActingOrg();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();

    const actingOrgId = acting?.actingOrgId ?? null;
    const orgId = searchParams.get('org') ?? actingOrgId;

    const canView = can('console.orgs.view');
    const enabled = authenticated && Boolean(accessToken) && Boolean(orgId) && canView;
    const canManage = Boolean(actingOrgId) && can(`org:${actingOrgId}.manage`);
    const canReset = can('console.accounts.reset');

    /* ------------------------------------------------------------ scope org */

    // Same query key as `useOrgTree`'s own root fetch, so navigating here from the
    // tree reuses the row already in cache instead of re-requesting it.
    const orgQuery = useQuery({
        queryKey: ['organization', accessToken, orgId],
        queryFn: () => fetchOrganization(config.apiBase, accessToken as string, orgId as string),
        enabled,
        retry: false,
    });

    const childrenQuery = useQuery({
        queryKey: ['org-children', accessToken, orgId],
        queryFn: () =>
            fetchOrganizations(config.apiBase, accessToken as string, {
                parentId: orgId,
                pageSize: MAX_SERVER_PAGE_SIZE,
                ordering: 'name',
            }),
        enabled,
        retry: false,
    });

    /*
     * EAA-1817 (2026-08-25, user report: "level is not apply for whole org even
     * child org"). TREE scope, not this org's own Levels.
     *
     * Every consumer of `activeLevels` on this screen is about accounts, not about
     * Level authoring: the bulk assign dialog, the inline picker, the CSV import
     * mapping, the provision modal, and the "Level" facet. A Level resolves against
     * the whole org tree server-side, so all of those can legitimately name a
     * parent's Level — and while this fetched only the scope org's rows, opening
     * the accounts list on a child đơn vị offered ONE Level where the tree has
     * seven, which is what made assigning from the child impossible.
     *
     * The facet wants the same list for the same reason: a row in scope can carry a
     * Level owned by an ancestor, so filtering by it must be offerable.
     *
     * NOT changed: `useHierarchyPage`'s own levels query. That screen AUTHORS
     * Levels — renaming or deactivating there must act on this org's own rows.
     */
    const levelsQuery = useQuery({
        queryKey: ['org-levels', accessToken, orgId, 'tree'],
        queryFn: () =>
            fetchOrgLevels(config.apiBase, accessToken as string, orgId as string, {
                scope: 'tree',
            }),
        enabled,
        retry: false,
    });

    const org = orgQuery.data ?? null;
    const childOrgs = useMemo<Organization[]>(
        () => childrenQuery.data?.results ?? [],
        [childrenQuery.data]
    );
    const activeLevels = useMemo(
        () => (levelsQuery.data?.results ?? []).filter((level) => level.is_active),
        [levelsQuery.data]
    );

    /** id → name for the "Đơn vị" column. Only the scope org and its direct children
     *  are known here; a deeper descendant falls back to a shortened id. */
    const orgNameById = useMemo(() => {
        const map = new Map<string, string>();
        if (org) map.set(org.id, org.name);
        for (const child of childOrgs) map.set(child.id, child.name);
        return map;
    }, [org, childOrgs]);

    /* --------------------------------------------------------- filter state */

    const [filters, setFilters] = useState<ScopedAccountsFilters>(EMPTY_FILTERS);
    const [sortField, setSortField] = useState<ScopedAccountSortField>('granted_at');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSizeState] = useState(PAGE_SIZE_OPTIONS[0]);

    /**
     * EAA-1839 (2026-08-21, user request): `GET /organizations/{id}/accounts`
     * takes no `search` param at all (`types.ts`'s own backend-contract note,
     * point 1) — so this is NOT a query param, ever. It drives a client-side
     * walk-and-filter, the same technique `useScopedAccountExport.collectRows`
     * already uses for its CSV, over `user_email`/`user_sub` — the only two
     * fields the row carries that could plausibly be "a name". See
     * `searchWalkQuery` below.
     */
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebouncedValue(search, 400);
    const searchActive = debouncedSearch.trim().length > 0;

    /**
     * EAA-1839 (2026-08-24, user request): one row per ACCOUNT, not per
     * assignment — see `groupScopedAccountRows`.
     *
     * WHY THIS FORCES THE WALK. The endpoint paginates assignments and orders by
     * `-granted_at` by default, so a person's assignments are NOT adjacent and can
     * land on different pages; grouping only the 20 rows of the current page would
     * still show the same account twice, which is the exact complaint. Grouping is
     * therefore computed over the SAME capped walk `searchActive` already uses, and
     * the board paginates the grouped result client-side. Both modes share one
     * query, so turning search on while grouped costs no second walk.
     *
     * The walk's cap is real and is surfaced (`searchCapped` below): past
     * `MAX_SELECT_ALL_PAGES * MAX_SERVER_PAGE_SIZE` assignments the grouped list
     * and its count are a PREFIX. Ungrouped server paging remains exact, which is
     * why this is a constant to flip rather than a fact buried in the render.
     */
    const groupByAccount = true;
    const walkActive = searchActive || groupByAccount;

    const filtersActive =
        filters.issue !== null ||
        filters.levelId !== null ||
        filters.childOrgId !== null ||
        filters.isActive !== null;

    const patchFilters = useCallback((patch: Partial<ScopedAccountsFilters>) => {
        setFilters((prev) => ({ ...prev, ...patch }));
        setPage(1);
    }, []);

    const selectIssue = useCallback(
        (value: ScopedAccountIssue) =>
            patchFilters({ issue: filters.issue === value ? null : value }),
        [filters.issue, patchFilters]
    );
    const selectLevel = useCallback(
        (value: string) => patchFilters({ levelId: filters.levelId === value ? null : value }),
        [filters.levelId, patchFilters]
    );
    const selectChildOrg = useCallback(
        (value: string) =>
            patchFilters({ childOrgId: filters.childOrgId === value ? null : value }),
        [filters.childOrgId, patchFilters]
    );
    const selectStatus = useCallback(
        (value: boolean) => patchFilters({ isActive: filters.isActive === value ? null : value }),
        [filters.isActive, patchFilters]
    );
    const clearFilters = useCallback(() => {
        setFilters((prev) => ({ ...EMPTY_FILTERS, includeChildren: prev.includeChildren }));
        setPage(1);
    }, []);

    const setPageSize = useCallback((size: number) => {
        setPageSizeState(size);
        setPage(1);
    }, []);

    const toggleSort = useCallback(
        (field: ScopedAccountSortField) => {
            if (field === sortField) {
                setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                return;
            }
            setSortField(field);
            setSortDirection(field === 'granted_at' ? 'desc' : 'asc');
        },
        [sortField]
    );

    /* ------------------------------------------------------------ the list */

    const listParams = useMemo<ScopedAccountsParams>(
        () => ({
            ...filterParams(filters),
            page,
            pageSize,
            ordering: orderingParam(sortField, sortDirection),
        }),
        [filters, page, pageSize, sortField, sortDirection]
    );

    const listQuery = useQuery({
        queryKey: ['scoped-accounts', accessToken, orgId, listParams],
        queryFn: () =>
            fetchScopedAccounts(config.apiBase, accessToken as string, orgId as string, listParams),
        // EAA-1839 (2026-08-24, user report: "api accounts have been call too
        // much"): `walkActive` (search OR grouping) means `rows`/`totalCount`
        // below come from `searchWalkQuery`'s client-side result, never from
        // `listQuery.data` — but this query had no gate reflecting that, so once
        // grouping made `walkActive` permanently true it fired on every load
        // anyway, wastefully paging the same endpoint twice (`page_size=20` here,
        // `page_size=100` in the walk) for a response nothing reads. Verified
        // live in the network panel before this fix.
        enabled: enabled && !walkActive,
        retry: false,
    });

    /**
     * EAA-1839: the search walk. Deliberately keyed WITHOUT `debouncedSearch` —
     * the walked rows depend only on scope/filters/sort, never on the search
     * text itself, so keying on the term as well would re-walk the same pages
     * from scratch every time the debounce settles on a new value. Filtering
     * happens client-side in `searchMatches` below instead, which means typing
     * after the first walk touches the network not at all, not merely "not per
     * keystroke". Same cap and same reason as `MAX_SELECT_ALL_PAGES` — the
     * endpoint's page ceiling is 100 and there is no "give me every row" mode.
     */
    const searchWalkQuery = useQuery({
        queryKey: ['scoped-accounts-search-walk', accessToken, orgId, filters, sortField, sortDirection],
        queryFn: async () => {
            const collected: ScopedAccountRow[] = [];
            let walkPage = 1;
            let capped = false;
            for (;;) {
                const res = await fetchScopedAccounts(
                    config.apiBase,
                    accessToken as string,
                    orgId as string,
                    {
                        ...filterParams(filters),
                        page: walkPage,
                        pageSize: MAX_SERVER_PAGE_SIZE,
                        ordering: orderingParam(sortField, sortDirection),
                    }
                );
                collected.push(...res.results);
                const reachedEnd = walkPage >= res.total_pages;
                if (reachedEnd) break;
                if (walkPage >= MAX_SELECT_ALL_PAGES) {
                    capped = true;
                    break;
                }
                walkPage += 1;
            }
            return { rows: collected, capped };
        },
        enabled: enabled && walkActive,
        retry: false,
    });

    const searchMatches = useMemo<ScopedAccountRow[]>(() => {
        if (!walkActive || !searchWalkQuery.data) return [];
        if (!searchActive) return searchWalkQuery.data.rows;
        const needle = debouncedSearch.trim().toLowerCase();
        return searchWalkQuery.data.rows.filter(
            (row) =>
                row.user_email.toLowerCase().includes(needle) ||
                row.user_sub.toLowerCase().includes(needle)
        );
    }, [walkActive, searchActive, searchWalkQuery.data, debouncedSearch]);

    /* The client-side result set, after grouping. Grouping happens AFTER the
       search filter so a term that matches one assignment surfaces that account
       once, rather than resurrecting its other assignments as extra rows. */
    const clientRows = useMemo<ScopedAccountGroupedRow[]>(
        () =>
            groupByAccount ? groupScopedAccountRows(searchMatches, orgId) : searchMatches,
        [groupByAccount, searchMatches, orgId]
    );

    // EAA-1839: a search term change re-pages to 1 for the same reason
    // `patchFilters` does — the client-side result set can be far shorter than
    // the page the operator was on.
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    const rows = useMemo<ScopedAccountGroupedRow[]>(() => {
        if (walkActive) return clientRows.slice((page - 1) * pageSize, page * pageSize);
        return listQuery.data?.results ?? [];
    }, [walkActive, clientRows, page, pageSize, listQuery.data]);
    /* Grouped: the number of ACCOUNTS, not assignments — the footer's "trong N"
       must agree with the rows above it, and an assignment count would not. */
    const totalCount = walkActive ? clientRows.length : listQuery.data?.count ?? 0;
    const totalPages = walkActive
        ? Math.max(1, Math.ceil(clientRows.length / pageSize))
        : Math.max(1, listQuery.data?.total_pages ?? 1);
    const rangeFrom = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
    const rangeTo = Math.min(page * pageSize, totalCount);
    /** EAA-1839: the search walk hit its page cap — the match set above is a
     *  PREFIX of what actually matches, so "no match" or a short list may be
     *  wrong. Surfaced next to the range readout (`ScopedAccountsPage.tsx`),
     *  not only here, because a code comment does not tell the operator anything. */
    const searchCapped = walkActive && Boolean(searchWalkQuery.data?.capped);
    const loading = walkActive
        ? searchWalkQuery.isLoading || orgQuery.isLoading
        : listQuery.isLoading || orgQuery.isLoading;
    const isError = walkActive
        ? searchWalkQuery.isError || orgQuery.isError
        : listQuery.isError || orgQuery.isError;

    const invalidateList = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: ['scoped-accounts'] });
        void queryClient.invalidateQueries({ queryKey: ['scoped-accounts-search-walk'] });
        void queryClient.invalidateQueries({ queryKey: ['scoped-accounts-facet'] });
    }, [queryClient]);

    /* ------------------------------------------------------- facet counting */

    // One `page_size=1` request per sidebar option — see `useScopedAccountFacets`
    // for why there is no aggregate endpoint to ask instead.
    const facetGroups = useScopedAccountFacets({
        apiBase: config.apiBase,
        token: accessToken,
        enabled,
        orgId,
        orgName: org?.name ?? null,
        childOrgs,
        activeLevels,
        filters,
        // EAA-1839 (2026-08-24, user report: "api accounts call too much"): the
        // same walk `rows`/`searchCapped` above already read — `groupByAccount`
        // being permanently on means this walk runs for every board render
        // anyway, so handing its rows to the facets hook lets it derive several
        // groups' counts instead of firing a `page_size=1` request per value.
        // `null` (not `[]`) while the walk has not resolved, so the facets hook
        // can tell "not answered yet" apart from "answered, zero rows".
        walkRows: walkActive ? (searchWalkQuery.data?.rows ?? null) : null,
        walkCapped: searchCapped,
    });

    /**
     * EAA-1839 (frame 35): the "Chưa có Level" number, read back off the facet
     * fan-out rather than fetched again. Frame 35 prints it in THREE places — the
     * tab badge, the saved-filter chip and frame 35a's "Áp dụng cho cả N tài khoản"
     * line — so it has to come from one source or the three will disagree by a
     * refresh interval.
     */
    const noLevelCount = useMemo(
        () =>
            facetGroups
                .find((group) => group.key === 'issue')
                ?.options.find((option) => option.key === 'no_level')?.count ?? null,
        [facetGroups]
    );

    const selectFacet = useCallback(
        (group: FacetGroupKey, key: string) => {
            if (group === 'issue') {
                selectIssue(key as ScopedAccountIssue);
                return;
            }
            if (group === 'level') {
                selectLevel(key);
                return;
            }
            if (group === 'childOrg') {
                selectChildOrg(key);
                return;
            }
            selectStatus(key === 'true');
        },
        [selectIssue, selectLevel, selectChildOrg, selectStatus]
    );

    /* ----------------------------------------------------------- selection */

    /**
     * Keyed by ASSIGNMENT id and holding the whole row, not just an id: the bulk
     * endpoints take `user_subs`, and "Chọn tất cả M dòng khớp bộ lọc" holds rows
     * that are not on the current page, so the rows have to be carried rather than
     * looked up in `rows`.
     */
    const [selected, setSelected] = useState<Map<string, ScopedAccountRow>>(new Map());
    const [selectAllBusy, setSelectAllBusy] = useState(false);
    const [selectAllCapped, setSelectAllCapped] = useState(false);

    const clearSelection = useCallback(() => {
        setSelected(new Map());
        setSelectAllCapped(false);
    }, []);

    // EAA-1839: a selection is dropped whenever the scope or any filter changes.
    // Pruning to the visible page (the `useRoleMapTable` rule) is wrong here —
    // that would silently undo "Chọn tất cả M dòng khớp bộ lọc", whose whole point
    // is holding rows off-page. Keeping it would be worse: it would let a bulk
    // action run against rows the operator can no longer see under the new filter.
    const filtersKey = JSON.stringify(filters);
    // EAA-1839: a search term change clears the selection for the exact same
    // reason a filter change does (comment above) — search narrows which rows
    // are ON SCREEN just as a filter does, so a bulk action could otherwise run
    // against rows the operator can no longer see.
    useEffect(() => {
        clearSelection();
    }, [orgId, filtersKey, debouncedSearch, clearSelection]);

    const toggleRow = useCallback((row: ScopedAccountRow) => {
        setSelected((prev) => {
            const next = new Map(prev);
            if (next.has(row.id)) next.delete(row.id);
            else next.set(row.id, row);
            return next;
        });
    }, []);

    const pageSelectedCount = rows.filter((row) => selected.has(row.id)).length;
    const allPageSelected = rows.length > 0 && pageSelectedCount === rows.length;
    const somePageSelected = pageSelectedCount > 0 && !allPageSelected;

    const toggleAll = useCallback(() => {
        setSelected((prev) => {
            const next = new Map(prev);
            const everySelected = rows.length > 0 && rows.every((row) => next.has(row.id));
            for (const row of rows) {
                if (everySelected) next.delete(row.id);
                else next.set(row.id, row);
            }
            return next;
        });
    }, [rows]);

    const selectAllMatching = useCallback(async () => {
        if (!orgId || !accessToken) return;
        setSelectAllBusy(true);
        setSelectAllCapped(false);
        const collected = new Map<string, ScopedAccountRow>();
        let walkPage = 1;
        let capped = false;
        try {
            for (;;) {
                const res = await fetchScopedAccounts(config.apiBase, accessToken, orgId, {
                    ...filterParams(filters),
                    page: walkPage,
                    pageSize: MAX_SERVER_PAGE_SIZE,
                    ordering: orderingParam(sortField, sortDirection),
                });
                for (const row of res.results) collected.set(row.id, row);
                const reachedEnd = walkPage >= res.total_pages;
                const hitCap = walkPage >= MAX_SELECT_ALL_PAGES;
                if (reachedEnd || hitCap) {
                    capped = hitCap && !reachedEnd;
                    break;
                }
                walkPage += 1;
            }
            setSelected(collected);
            setSelectAllCapped(capped);
        } finally {
            setSelectAllBusy(false);
        }
    }, [orgId, accessToken, config.apiBase, filters, sortField, sortDirection]);

    const selectedRows = useMemo(() => [...selected.values()], [selected]);

    /**
     * EAA-1839: the subs a bulk Level-assign may legally target — those whose
     * grant lives in the SCOPE org itself.
     *
     * This exclusion is not defensive tidiness, it prevents a silent double-grant.
     * `AssignmentService.bulk_preview` classifies against
     * `Assignment.objects.filter(organization_id=org_id, ...)` — the URL org only,
     * with no `include_children` recursion (its own comment: "Deliberately
     * single-org"). A row belonging to a descendant đơn vị therefore looks like
     * `new`, and `bulk_apply` would CREATE a second active grant in the parent org
     * while leaving the descendant grant untouched, rather than changing the level
     * the operator was looking at. The duplicate guard does not catch it either —
     * `_has_active_duplicate` keys on (user_sub, level_id), and these are two
     * different levels. So cross-đơn-vị rows are excluded and counted out loud.
     */
    /*
     * EAA-1817 (2026-08-25, user instruction: "level must cross ORG even child
     * ORG"). The selection is now GROUPED by đơn vị instead of filtered down to
     * the scope org.
     *
     * The old exclusion was right about the mechanism and wrong about the remedy.
     * `bulk_apply` resolves against the org in the PATH, so sending a descendant's
     * row to the scope org's endpoint would add a second active grant in the
     * parent rather than change the one on screen — that hazard is unchanged. But
     * dropping those rows meant "Chọn tất cả 39" quietly assigned 28 of them,
     * which is what the reporter hit. Running one call per đơn vị writes each row
     * into its OWN org, which is what the operator meant by selecting it.
     *
     * What made this possible is the matching server change: a Level now resolves
     * against the whole org TREE (`OrganizationService.list_tree_ids`), so the
     * parent's Level is legal on a child's account. Before that, a per-org call
     * would have been rejected as `valid_level: false`.
     */
    const bulkTargetsByOrg = useMemo(() => {
        const byOrg = new Map<string, string[]>();
        for (const row of selectedRows) {
            const subs = byOrg.get(row.organization_id);
            if (subs) {
                if (!subs.includes(row.user_sub)) subs.push(row.user_sub);
                continue;
            }
            byOrg.set(row.organization_id, [row.user_sub]);
        }
        return [...byOrg.entries()].map(([organizationId, userSubs]) => ({
            organizationId,
            userSubs,
        }));
    }, [selectedRows]);

    const bulkUserSubs = useMemo(
        () => [...new Set(selectedRows.map((row) => row.user_sub))],
        [selectedRows]
    );
    /** Kept for the bulk bar's note; no longer a count of rows left behind. */
    const crossOrgSelectedCount = useMemo(
        () => selectedRows.filter((row) => row.organization_id !== orgId).length,
        [selectedRows, orgId]
    );

    /* ----------------------------------------------- honest-gap explanations */

    /** `null` when bulk Level-assign is available; otherwise why it is not. */
    let bulkLevelDisabledReason: string | null = null;
    if (!canManage) {
        bulkLevelDisabledReason = t(
            'console.hierarchy.scopedAccounts.bulkLevel.noManagePermission'
        );
    } else if (activeLevels.length === 0) {
        bulkLevelDisabledReason = t(
            'console.hierarchy.scopedAccounts.bulkLevel.noActiveLevels'
        );
    } else if (bulkUserSubs.length === 0) {
        bulkLevelDisabledReason =
            crossOrgSelectedCount > 0
                ? t('console.hierarchy.scopedAccounts.bulkLevel.crossOrgOnly')
                : t('console.hierarchy.scopedAccounts.bulkLevel.noSelection');
    }

    return {
        enabled,
        canView,
        canManage,
        canReset,

        orgId,
        org,
        orgName: org?.name ?? null,
        orgQuery,
        orgNameById,
        activeLevels,

        filters,
        // EAA-1820: exposed (not just `patchFilters`) so the shared screen's
        // "Lưu bộ lọc hiện tại" can restore a WHOLE snapshot in one call —
        // every individual `select*` helper only ever changes one field.
        setFilters,
        filtersActive,
        facetGroups,
        selectFacet,
        clearFilters,
        noLevelCount,

        search,
        setSearch,
        searchCapped,

        listQuery,
        rows,
        totalCount,
        loading,
        isError,
        error: (searchActive ? searchWalkQuery.error : listQuery.error) ?? (orgQuery.error as unknown),
        invalidateList,

        page,
        setPage,
        pageSize,
        setPageSize,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        totalPages,
        rangeFrom,
        rangeTo,

        sortField,
        sortDirection,
        toggleSort,

        selected,
        selectedRows,
        selectedCount: selected.size,
        pageSelectedCount,
        allPageSelected,
        somePageSelected,
        toggleRow,
        toggleAll,
        clearSelection,
        selectAllMatching,
        selectAllBusy,
        selectAllCapped,
        maxSelectAllRows: MAX_SELECT_ALL_PAGES * MAX_SERVER_PAGE_SIZE,

        /** EAA-1817: pickers must not read an in-flight list as "no Levels here". */
        levelsLoading: levelsQuery.isLoading,
        bulkUserSubs,
        bulkTargetsByOrg,
        crossOrgSelectedCount,
        bulkLevelDisabledReason,
    };
}

export type ScopedAccountsVM = ReturnType<typeof useScopedAccounts>;
